import React, { useEffect, useRef } from "react";

/**
 * Decorative gear/tyre that rotates based on the page scroll position.
 * Pointer-events disabled so it never blocks UI.
 */
export const ScrollGear = ({
  size = 220,
  speed = 0.4,
  variant = "gear",
  className = "",
  style = {},
}) => {
  const ref = useRef(null);

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        if (ref.current) {
          const rotation = window.scrollY * speed;
          ref.current.style.transform = `rotate(${rotation}deg)`;
        }
        raf = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [speed]);

  const Inner = variant === "tyre" ? TyreSVG : GearSVG;

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none select-none ${className}`}
      style={style}
    >
      <div ref={ref} style={{ width: size, height: size, willChange: "transform" }}>
        <Inner />
      </div>
    </div>
  );
};

const GearSVG = () => (
  <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none">
    <g stroke="#E11D2B" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round">
      {Array.from({ length: 12 }).map((_, i) => {
        const angle = (i * 360) / 12;
        return (
          <rect
            key={`tooth-${i}`}
            x="47.5"
            y="2"
            width="5"
            height="10"
            transform={`rotate(${angle} 50 50)`}
            fill="#E11D2B"
          />
        );
      })}
      <circle cx="50" cy="50" r="34" fill="none" />
      <circle cx="50" cy="50" r="30" fill="none" />
      <circle cx="50" cy="50" r="10" fill="none" />
      {Array.from({ length: 6 }).map((_, i) => {
        const angle = (i * 360) / 6;
        return (
          <line
            key={`spoke-${i}`}
            x1="50"
            y1="20"
            x2="50"
            y2="35"
            transform={`rotate(${angle} 50 50)`}
          />
        );
      })}
    </g>
  </svg>
);

const TyreSVG = () => (
  <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none">
    {/* Outer tread */}
    <circle cx="50" cy="50" r="46" stroke="#27272A" strokeWidth="6" fill="#09090B" />
    {/* tread blocks */}
    {Array.from({ length: 24 }).map((_, i) => {
      const angle = (i * 360) / 24;
      return (
        <rect
          key={`tread-${i}`}
          x="48"
          y="3"
          width="4"
          height="6"
          fill="#3F3F46"
          transform={`rotate(${angle} 50 50)`}
        />
      );
    })}
    {/* Sidewall */}
    <circle cx="50" cy="50" r="36" stroke="#52525B" strokeWidth="1" fill="none" />
    {/* Alloy spokes */}
    <circle cx="50" cy="50" r="20" fill="#18181B" stroke="#E11D2B" strokeWidth="1.5" />
    {Array.from({ length: 5 }).map((_, i) => {
      const angle = (i * 360) / 5;
      return (
        <rect
          key={`alloy-${i}`}
          x="48.5"
          y="30"
          width="3"
          height="22"
          fill="#27272A"
          stroke="#52525B"
          strokeWidth="0.5"
          transform={`rotate(${angle} 50 50)`}
        />
      );
    })}
    <circle cx="50" cy="50" r="5" fill="#E11D2B" />
  </svg>
);

const ScrewSVG = () => (
  <svg viewBox="0 0 100 100" width="100%" height="100%" fill="none">
    <circle cx="50" cy="50" r="40" fill="#27272A" stroke="#52525B" strokeWidth="2" />
    <rect x="20" y="46" width="60" height="8" fill="#71717A" rx="1" />
    <rect x="46" y="20" width="8" height="60" fill="#71717A" rx="1" />
  </svg>
);

export const ScrollScrew = ({ size = 80, speed = 0.6, className = "", style = {} }) => {
  const ref = useRef(null);
  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf !== null) return;
      raf = requestAnimationFrame(() => {
        if (ref.current) {
          ref.current.style.transform = `rotate(${window.scrollY * speed}deg)`;
        }
        raf = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [speed]);
  return (
    <div aria-hidden="true" className={`pointer-events-none select-none ${className}`} style={style}>
      <div ref={ref} style={{ width: size, height: size, willChange: "transform" }}>
        <ScrewSVG />
      </div>
    </div>
  );
};
